/**
 * Jurisdiction Resolver
 *
 * 현재 세션의 jurisdiction(MY / SG)을 표준 코드(`'MY'` | `'SG'`)로 정규화하고,
 * jurisdiction 별 분기 헬퍼를 제공.
 *
 * 다른 곳에서 'MALAYSIA'/'MY', 'SINGAPORE'/'SG' 가 섞여 들어오는 경우가 많아
 * 한 곳에서 정규화한다.
 */

import { useSessionStore } from '@/shared/stores/sessionStore';

export type Jurisdiction = 'MY' | 'SG';

/** 'MALAYSIA'/'MY'/'my' 등 다양한 표기를 'MY'/'SG'/null 로 정규화. */
export function normalizeJurisdiction(raw: unknown): Jurisdiction | null {
  if (!raw || typeof raw !== 'string') return null;
  const v = raw.trim().toUpperCase();
  if (v === 'MY' || v === 'MALAYSIA') return 'MY';
  if (v === 'SG' || v === 'SINGAPORE') return 'SG';
  return null;
}

/** 현재 세션의 jurisdiction. 못 알아내면 null. */
export function getCurrentJurisdiction(): Jurisdiction | null {
  const sessionStore = useSessionStore();
  const raw = (sessionStore as any)?.user?.customer?.jurisdiction;
  return normalizeJurisdiction(raw);
}

/** 편의 boolean. */
export const isMy = (): boolean => getCurrentJurisdiction() === 'MY';
export const isSg = (): boolean => getCurrentJurisdiction() === 'SG';

/**
 * 현재 환경/세션에서 PreSet 모달을 활성화할지 결정.
 *   MY / SG — 모든 환경에서 활성 (운영 포함)
 *   그 외 jurisdiction — 비활성
 */
export function isPreSetSupported(): boolean {
  if (isMy()) return true;
  if (isSg()) return true;
  return false;
}

/**
 * Task Code prefix 로 추정한 jurisdiction.
 *   M* → MY, S* → SG, 그 외(B*, M3* 공통 등) → null
 *   세션 jurisdiction 보다 task code 가 우선이어야 하는 케이스에서 사용.
 */
export function jurisdictionFromTaskCode(taskConfigCd: string | null | undefined): Jurisdiction | null {
  if (!taskConfigCd) return null;
  const c = taskConfigCd.trim().toUpperCase();
  if (c.startsWith('M')) return 'MY';
  if (c.startsWith('S')) return 'SG';
  return null;
}
